import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

// Brand email design (admin-api routes/brand-email-theme.ts, ADR-0012): the
// acting brand's colour tokens, letterhead identity and style, versioned, plus a
// gallery of every email the brand sends rendered from an unsaved design.

export interface EmailTokens {
  page: string;
  card: string;
  border: string;
  rule: string;
  panel: string;
  panelBorder: string;
  heading: string;
  strong: string;
  body: string;
  label: string;
  muted: string;
  faint: string;
  accent: string;
  button: string;
  buttonText: string;
  onAccent: string;
  highlight: string;
}

/** The identity an admin may set. Brand name, site and sender details come from the brand registry. */
export interface EmailIdentity {
  wordmark?: string;
  /** The end of the wordmark shown in the accent colour, e.g. "IQ". */
  wordmarkAccent?: string;
  tagline?: string;
  logoUrl?: string | null;
  logoWidth?: number;
  logoHeight?: number;
  footerTaglineLead?: string;
  footerTaglineAccent?: string;
  signOffName?: string;
  /** The welcome email's sentence after "Welcome to <brand>." Empty: generic wording. */
  welcomeIntro?: string;
  /** The welcome email's "Your account provides" list. Empty: a generic list. */
  welcomeFeatures?: string[];
}

/** Typography and shape. Font keys come from the `fonts` list the server sends. */
export interface EmailStyle {
  headingFont: string;
  bodyFont: string;
  /** Card and panel corner radius in px, 0-16. */
  radius: number;
  headlineCase: 'upper' | 'sentence';
}

export interface EmailFont {
  key: string;
  label: string;
  stack: string;
}

export interface EmailChecks {
  errors: string[];
  warnings: string[];
}

export interface EmailThemeVersion {
  version: number;
  tokens: EmailTokens;
  identity: EmailIdentity;
  /** Older rows may hold `{}`; missing keys mean the starter value. */
  style: Partial<EmailStyle> | null;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
}

export interface EmailThemeState {
  active: EmailThemeVersion | null;
  versions: EmailThemeVersion[];
  starter: { tokens: EmailTokens; identity: EmailIdentity; style: EmailStyle };
  fonts: EmailFont[];
  checks: EmailChecks | null;
}

export interface EmailDesign {
  tokens: EmailTokens;
  identity: EmailIdentity;
  style: EmailStyle;
}

export interface PreviewEmail {
  key: string;
  name: string;
  trigger: string;
  subject: string;
  preheader: string;
  html: string;
}

export interface EmailPreview {
  emails: PreviewEmail[];
  checks: EmailChecks;
}

const THEME_KEY = ['brand-settings', 'email-theme'];

export function useEmailTheme() {
  return useQuery({ queryKey: THEME_KEY, queryFn: () => apiClient<EmailThemeState>('/brand-settings/email-theme') });
}

/** Every email the brand sends, built from the given (unsaved) design. Pass null to skip. */
export function useEmailPreview(design: EmailDesign | null) {
  return useQuery({
    queryKey: [...THEME_KEY, 'preview', design],
    queryFn: () =>
      apiClient<EmailPreview>('/brand-settings/email-theme/preview', { method: 'POST', body: JSON.stringify(design) }),
    enabled: design !== null,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Sends one sample email built from the design to the signed-in admin (or `to`). */
export function useSendTestEmail() {
  return useMutation({
    mutationFn: (input: EmailDesign & { key: string; to?: string }) =>
      apiClient<{ sentTo: string }>('/brand-settings/email-theme/test', { method: 'POST', body: JSON.stringify(input) }),
  });
}

function useThemeMutation<TInput, TResult>(request: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: THEME_KEY, exact: true }),
  });
}

/** Saves the design as a new version and makes it live. */
export function useSaveEmailTheme() {
  return useThemeMutation((design: EmailDesign) =>
    apiClient<{ theme: EmailThemeVersion; checks: EmailChecks }>('/brand-settings/email-theme', {
      method: 'POST',
      body: JSON.stringify(design),
    }),
  );
}

/** Makes an earlier version live again. */
export function useActivateEmailTheme() {
  return useThemeMutation((version: number) =>
    apiClient<{ theme: EmailThemeVersion }>(`/brand-settings/email-theme/versions/${version}/activate`, { method: 'POST' }),
  );
}

/** Switches the design off: the brand goes back to its old emails. */
export function useDeactivateEmailTheme() {
  return useThemeMutation(() => apiClient<{ success: boolean }>('/brand-settings/email-theme/deactivate', { method: 'POST' }));
}
